/**
 * Trailing MFE/MAE for diamond signals — SIMULATED, not traded.
 *
 * Two rows of raw numbers, one column per trade, newest on the right and
 * scrollable horizontally. It answers "how much heat does a six-point target
 * sit through", which is the question the fixed bracket makes worth asking.
 *
 * The MFE cell reads `capped (true)`: `6.0 (9.25)` banked its six while the
 * market went on to offer nine and a quarter. Equal values mean the move never
 * reached the target, so nothing was left behind.
 *
 * WHAT THIS IS NOT: an invert signal. `MAE <= -6` counts how often a fade would
 * have been TOUCHED, not what a fade COSTS when it is not. At 6 points of
 * target against 105 of stop those are very different questions — the trades
 * that do not reach -6 are not small losses when inverted, they are the whole
 * stop. Settling that needs the fade simulated with its own bracket and the
 * P&L compared, which is not this. The count is shown because it is the honest
 * reading of the data, with the caveat attached rather than left to be
 * discovered.
 *
 * Unfilled signals are absent, not zero. They never held a position, so they
 * have no excursion; a zero cell would read as "no heat taken".
 */

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useCandidateStore } from '../../store/candidateStore';
import type { EngineDiamond } from '../../utils/setupCandidates';
import './DiamondSignalExcursionOverlay.css';

const COLLAPSE_KEY = 'dsx:collapsed';

/** How many trades the strip holds. */
const TRAILING = 30;

/**
 * Every time shown is PACIFIC, never the browser's zone.
 *
 * The session this bot trades is defined in PT — the 1pm flatten, the 15:00
 * roll — so a timestamp rendered in whatever zone the viewer happens to sit in
 * cannot be compared against any of it. Reading "20:14" on a phone in another
 * zone and matching it against a 1pm rule is a mistake waiting to happen.
 */
const PT = 'America/Los_Angeles';

const PT_HHMM: Intl.DateTimeFormatOptions = {
  timeZone: PT,
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
};

const PT_DAY: Intl.DateTimeFormatOptions = { timeZone: PT, day: '2-digit' };

const PT_DATEKEY: Intl.DateTimeFormatOptions = {
  timeZone: PT,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
};

const PT_FULL: Intl.DateTimeFormatOptions = {
  timeZone: PT,
  month: 'short',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
};

/** Results that actually held a position, and so have an excursion. */
const HELD = new Set(['win', 'loss', 'flat', 'ambiguous']);

/** Single letter per outcome — the column is too narrow for a word. */
const MARK: Record<string, string> = {
  win: 'W',
  loss: 'L',
  flat: 'F',
  ambiguous: '?',
};

interface Row {
  ts: number;
  /**
   * What the column prints under the outcome letter.
   *
   * `06:14` inside a day, `27 06:14` on the first trade of a new one. Thirty
   * trades routinely span several sessions, so a bare clock cannot say which
   * signal a column is — and on a phone there is no hover to fall back on.
   */
  stamp: string;
  direction: string;
  result: string;
  mae: number;
  mfe: number;
  mfeCapped: number;
  entry: number;
}

function fmt(n: number): string {
  return n.toFixed(2);
}

function median(xs: number[]): number {
  if (!xs.length) return 0;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
}

export const DiamondSignalExcursionOverlay: React.FC = () => {
  const engineDiamonds = useCandidateStore((s) => s.engineDiamonds);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1';
    } catch {
      return false;
    }
  });

  /**
   * A vertical wheel over the strip scrolls it sideways.
   *
   * Without this the only way to reach older trades on a mouse is to drag the
   * 6px scrollbar, which is the reason "cannot scroll" is the first thing
   * anyone says about a horizontal strip. Non-passive so preventDefault holds.
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const onWheel = (e: WheelEvent) => {
      if (e.deltaY === 0) return;
      // Let a genuinely horizontal gesture (trackpad) through untouched.
      if (Math.abs(e.deltaX) > Math.abs(e.deltaY)) return;
      e.preventDefault();
      el.scrollLeft += e.deltaY;
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [collapsed]);

  /**
   * Keep touch gestures on the strip.
   *
   * The panel floats over a TradingView canvas whose handlers claim touch for
   * panning the chart. `touch-action: pan-x` tells the browser this box wants
   * horizontal drags, but the canvas can still receive the event by bubbling
   * and act on it, so the drag has to stop here as well. Vertical movement is
   * left alone — the page must still scroll from over the panel.
   */
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    let x = 0;
    let y = 0;
    const onStart = (e: TouchEvent) => {
      x = e.touches[0].clientX;
      y = e.touches[0].clientY;
    };
    const onMove = (e: TouchEvent) => {
      const dx = e.touches[0].clientX - x;
      const dy = e.touches[0].clientY - y;
      if (Math.abs(dx) <= Math.abs(dy)) return; // a vertical swipe is the page's
      e.stopPropagation();
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
    };
  }, [collapsed]);

  const toggle = () => {
    setCollapsed((c) => {
      try {
        localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1');
      } catch {
        // Preference just does not persist; the control still works.
      }
      return !c;
    });
  };

  const { rows, tp } = useMemo(() => {
    const all = Object.values(engineDiamonds) as EngineDiamond[];
    const held: Row[] = [];
    let target = 6;

    for (const d of all) {
      const s = d.simulated;
      // Both guards matter: a result that never held has no excursion, and a
      // null excursion is the engine saying it could not measure one. Either
      // way there is nothing to draw.
      if (!s || !HELD.has(s.result)) continue;
      if (s.mae == null || s.mfe == null) continue;
      target = s.tpPoints ?? target;
      held.push({
        ts: d.timestamp,
        stamp: '',
        direction: d.direction,
        result: s.result,
        mae: s.mae,
        mfe: s.mfe,
        mfeCapped: s.mfeCapped ?? Math.min(s.mfe, s.tpPoints ?? target),
        entry: s.entry,
      });
    }

    held.sort((a, b) => a.ts - b.ts);
    const window = held.slice(-TRAILING);

    // Day shown only when it changes, so the date is present without repeating
    // it thirty times in a strip this narrow.
    let prevDay = '';
    for (const r of window) {
      const d = new Date(r.ts);
      const key = d.toLocaleDateString('en-US', PT_DATEKEY);
      const hhmm = d.toLocaleTimeString('en-US', PT_HHMM);
      r.stamp =
        key === prevDay ? hhmm : `${d.toLocaleDateString('en-US', PT_DAY)} ${hhmm}`;
      prevDay = key;
    }

    return { rows: window, tp: target };
  }, [engineDiamonds]);

  /**
   * Open on the NEWEST trade, with older ones to the left.
   *
   * The strip is ordered oldest-to-newest so it reads the same way as the
   * chart, which puts the trade you actually care about off the right edge at
   * rest. Layout effect rather than effect: doing it after paint shows a frame
   * parked on the oldest trade first.
   */
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollLeft = el.scrollWidth;
  }, [rows.length, collapsed]);

  const maes = rows.map((r) => r.mae);
  const mfes = rows.map((r) => r.mfe);
  const beyondTarget = maes.filter((m) => m <= -tp).length;

  if (!rows.length) {
    return (
      <div className="dsx">
        <div className="dsx-head">
          <span className="dsx-title">DIAMOND SIGNAL MFE/MAE</span>
          <span className="dsx-meta">no resolved trades yet</span>
        </div>
      </div>
    );
  }

  return (
    <div className="dsx">
      <div className="dsx-head" onClick={toggle} title="Click to collapse">
        <span className="dsx-title">DIAMOND SIGNAL MFE/MAE</span>
        <span className="dsx-meta">
          trailing {rows.length} · TP {tp} · times PT · flat 1pm PT
        </span>
      </div>

      {!collapsed && (
        <>
          <div className="dsx-strip">
            <div className="dsx-labels">
              <div className="dsx-label">MFE</div>
              <div className="dsx-label">MAE</div>
              <div className="dsx-label dsx-label-dim">·</div>
            </div>
            <div className="dsx-scroll" ref={scrollRef}>
              <div className="dsx-grid">
                {rows.map((r) => {
                  const left = r.mfe > r.mfeCapped;
                  return (
                    <div
                      key={r.ts}
                      className="dsx-col"
                      title={
                        `${new Date(r.ts).toLocaleString('en-US', PT_FULL)} PT  ` +
                        `${r.direction.toUpperCase()}\n` +
                        `entry ${r.entry}\n` +
                        `MFE  ${fmt(r.mfeCapped)} taken, ${fmt(r.mfe)} offered\n` +
                        `MAE  ${fmt(r.mae)}\n` +
                        `exit ${r.result}`
                      }
                    >
                      <div className="dsx-cell dsx-mfe">
                        <span className="dsx-num">{fmt(r.mfeCapped)}</span>
                        {left && <span className="dsx-true">({fmt(r.mfe)})</span>}
                      </div>
                      <div
                        className={`dsx-cell dsx-mae${r.mae <= -tp ? ' dsx-hot' : ''}`}
                      >
                        <span className="dsx-num">{fmt(r.mae)}</span>
                      </div>
                      <div className={`dsx-cell dsx-mark dsx-mark-${r.result}`}>
                        {MARK[r.result] ?? '·'}
                        <span className="dsx-time">
                          {r.stamp}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="dsx-foot">
            <span>
              MAE med <b>{fmt(median(maes))}</b> · worst <b>{fmt(Math.min(...maes))}</b>
            </span>
            <span>
              MFE med <b>{fmt(median(mfes))}</b> offered
            </span>
            <span
              className="dsx-caveat"
              title={
                'How often a fade would have been TOUCHED — not what it costs when it is not. ' +
                `At ${tp} points of target against a much wider stop those are different questions, ` +
                'and this count does not settle whether to invert.'
              }
            >
              MAE ≤ −{tp} on{' '}
              <b>
                {beyondTarget}/{rows.length}
              </b>{' '}
              ({Math.round((100 * beyondTarget) / rows.length)}%)
            </span>
          </div>
        </>
      )}
    </div>
  );
};

export default DiamondSignalExcursionOverlay;
